import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateNPS, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const NPSCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [monthlyContribution, setMonthlyContribution] = useState(10000);
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [expectedReturn, setExpectedReturn] = useState(10);
  const [annuityPercent, setAnnuityPercent] = useState(40);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [monthlyContribution, currentAge, retirementAge, expectedReturn, annuityPercent]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const mc = Number(monthlyContribution) || 0;
    const ca = Number(currentAge) || 0;
    const ra = Number(retirementAge) || 0;
    const er = Number(expectedReturn) || 0;
    const ap = Number(annuityPercent) || 0;
    const npsResult = calculateNPS(
      mc, ca, ra, er, ap
    );
    setResult(npsResult);
  };

  const handleDownloadPDF = () => {
    generateGenericPDF('NPS Calculator Report', {
      'Monthly Contribution': monthlyContribution,
      'Current Age': currentAge,
      'Retirement Age': retirementAge,
      'Expected Return': `${expectedReturn}%`,
      'Annuity Percentage': `${annuityPercent}%`,
      'Total Invested': result.totalInvested,
      'Total Corpus': result.totalCorpus,
      'Wealth Gained': result.wealthGained,
      'Lumpsum Amount': result.lumpsumAmount,
      'Annuity Investment': result.annuityAmount,
      'Est. Monthly Pension': result.estimatedMonthlyPension,
    });
  };

  const handleReset = () => {
    setMonthlyContribution(10000);
    setCurrentAge(30);
    setRetirementAge(60);
    setExpectedReturn(10);
    setAnnuityPercent(40);
  };

  const chartData = result ? {
    labels: ['Total Invested', 'Returns Earned'],
    datasets: [{
      data: [result.totalInvested, result.wealthGained],
      backgroundColor: ['#14b8a6', '#f59e0b'],
      borderColor: ['#0d9488', '#d97706'],
      borderWidth: 2,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="NPS Calculator"
        description="Free NPS Calculator India - Calculate National Pension Scheme returns. Plan retirement corpus with pension estimate."
        keywords="NPS calculator, NPS calculator India, National Pension Scheme calculator, NPS pension calculator, NPS retirement calculator"
        canonicalPath="/nps"
      />
      <div className="calculator-header">
        <h1>NPS Calculator</h1>
        <p>Plan your National Pension System investment for retirement</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Monthly Contribution (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={monthlyContribution === '' ? '' : monthlyContribution}
                onChange={(e) => setMonthlyContribution(e.target.value === '' ? '' : Number(e.target.value))}
                min="500"
                max="500000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="500"
                max="100000"
                step="500"
                value={monthlyContribution || 0}
                onChange={(e) => setMonthlyContribution(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((monthlyContribution || 0) / 100000) * 100}%` }}
              />
            </div>
          </div>

          <div className="grid-2-cols">
            <div className="input-group">
              <label>Current Age</label>
              <input
                type="number"
                value={currentAge === '' ? '' : currentAge}
                onChange={(e) => setCurrentAge(e.target.value === '' ? '' : Number(e.target.value))}
                min="18"
                max="65"
              />
            </div>

            <div className="input-group">
              <label>Retirement Age</label>
              <input
                type="number"
                value={retirementAge === '' ? '' : retirementAge}
                onChange={(e) => setRetirementAge(e.target.value === '' ? '' : Number(e.target.value))}
                min="60"
                max="75"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Expected Return (% p.a.)</label>
            <input
              type="number"
              value={expectedReturn === '' ? '' : expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
              min="8"
              max="14"
              step="0.5"
            />
            <div className="slider-container">
              <input
                type="range"
                min="8"
                max="14"
                step="0.5"
                value={expectedReturn || 0}
                onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${(((expectedReturn || 0) - 8) / 6) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Annuity Purchase (% of corpus)</label>
            <input
              type="number"
              value={annuityPercent === '' ? '' : annuityPercent}
              onChange={(e) => setAnnuityPercent(e.target.value === '' ? '' : Number(e.target.value))}
              min="40"
              max="100"
            />
            <div className="slider-container">
              <input
                type="range"
                min="40"
                max="100"
                step="5"
                value={annuityPercent || 0}
                onChange={(e) => setAnnuityPercent(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${(((annuityPercent || 0) - 40) / 60) * 100}%` }}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              Minimum 40% must be used to purchase annuity
            </small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Total Corpus at Retirement</h3>
                <div className="amount">{formatCurrency(result.totalCorpus)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Investment</span>
                  <span className="value">{formatCurrency(result.totalInvested)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Returns Earned</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(result.wealthGained)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Lumpsum (Tax-free)</span>
                  <span className="value">{formatCurrency(result.lumpsumAmount)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Annuity Investment</span>
                  <span className="value">{formatCurrency(result.annuityAmount)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Est. Monthly Pension</span>
                  <span className="value" style={{ color: '#2563eb', fontWeight: 'bold' }}>
                    {formatCurrency(result.estimatedMonthlyPension)}
                  </span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About NPS (National Pension System)</h4>
        <ul>
          <li><strong>Tax Benefit:</strong> Additional ₹50,000 deduction under 80CCD(1B)</li>
          <li><strong>Withdrawal:</strong> Up to 60% can be withdrawn as tax-free lumpsum</li>
          <li><strong>Minimum 40%:</strong> Must be used to purchase annuity for monthly pension</li>
          <li><strong>Lock-in:</strong> Till age 60 (partial withdrawal allowed for specific purposes)</li>
          <li><strong>Asset Classes:</strong> Equity (E), Corporate Bonds (C), Government Securities (G)</li>
        </ul>
      </div>
    </div>
  );
};

export default NPSCalculator;
